import React, { useContext, useEffect, useRef, useState } from 'react'
import OrderSummary from './order-summary/OrderSummary'
import SecureCheckoutForm from './SecureCheckoutForm'
import * as API from '../../../src/util/API'
import * as GTM from '../../../src/util/GTM'
import CreatingYourAccount from './CreatingYourAccount'
import Alert, { Color as AlertColor } from '../../design-components/Alert'
import { AccountDetails } from '../../types'
import { injectStripe, Stripe } from 'react-stripe-elements'
import ReCAPTCHA from 'react-google-recaptcha'
import { ValidationError } from '../../util/Validations'
import { createStyles, makeStyles } from '@material-ui/core'
import AccountCreated from './AccountCreated'
import Spinner from 'spinner'
import orderSummaryContext from './order-summary/OrderSummaryContext'
import OrderLayout, { OrderLayoutLeftColumn, OrderLayoutRightColumn } from '../OrderLayout'
import LeadContext, { LeadContextValue } from '../../LeadContext'
import SubmitButton from '../../design-components/SubmitButton'
import CheckoutBadges from './CheckoutBadges'
import BusinessInfoForm from './BusinessInfoForm'
import { useHistory } from 'react-router-dom'
import TermsAndConditions from './TermsAndConditions'

const useStyles = makeStyles(theme =>
    createStyles({
        form: {
            margin: '0 auto',
            maxWidth: '810px'
        },
        submitContainer: {
            marginTop: '30px',
            marginBottom: '20px'
        },
        recaptchaContainer: {
            position: 'relative',
            zIndex: 1
        }
    })
)

interface CheckoutProps {
    stripe: Stripe;
    setShowStepper: (showStepper: boolean) => void;
    setDisableStepper: (disableStepper: boolean) => void;
}

/**
 * Checkout step of sign-up
 */
const Checkout = ({ stripe, setShowStepper, setDisableStepper } : CheckoutProps) : JSX.Element => {
    const classes = useStyles()
    const history = useHistory()

    const { saveLeadParams, performMFASteps, leadParams, invalidateSelectedNumber } = useContext<LeadContextValue>(LeadContext)

    const orderSummary = useContext(orderSummaryContext)
    const { bill } = orderSummary

    const [error, setError] = useState<Error | null>(null)
    const [processing, setProcessing] = useState(false)
    const [isCreatingAccount, setIsCreatingAccount] = useState<boolean>(false)
    const [accountDetails, setAccountDetails] = useState<AccountDetails | null>(null)

    const [receiveSMS, setReceiveSMS] = useState<boolean>(false)
    const [receiveSMSOffers, setReceiveSMSOffers] = useState<boolean>(false)

    const recaptchaRef = useRef()

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search)
        if (searchParams.get('returned_from_mfa')) {
            console.info('MFA: User has returned from the MFA app, starting the account creation process...')
            createAccount()
        }
    }, [])

    const onSubmit = async event => {
        event.preventDefault()

        if (processing) {
            return // Prevent submitting again by pressing enter, etc.
        }

        setError(null) // clear any previous error
        setProcessing(true)

        const tasks = [
            { fn: () => saveLead(), description: 'saving lead data' },
            { fn: () => savePaymentToken(), description: 'payment information processing' }
        ]

        if (leadParams.enable_recaptcha) {
            tasks.push(
                { fn: () => verifyHumanUser(), description: 'human verification' }
            )
        }

        try {
            for (const task of tasks) {
                await task.fn().catch(error => {
                    if (error instanceof ValidationError) {
                        GTM.validationError(error.message, error.errorCode)
                    } else {
                        GTM.checkoutError(error.message, task.description)
                    }
                    throw error
                })
            }

            const canSkip = performMFASteps()
            if (canSkip) {
                console.info('MFA: skipping MFA setup and starting account creation process...')
                createAccount()
            }
        } catch (error) {
            onError(error)
        }
    }

    const onError = (error: Error) => {
        console.info('Submission error: ', error)

        if (error instanceof API.CreateAccountError) {
            const errorType = error.createAccountErrors[0].error_type
            if (errorType === 'Number Purchase Error') {
                invalidateSelectedNumber('number_not_available')
                history.push('/pick-a-number')
                return
            }
        }

        setError(error)
        setProcessing(false)
        setIsCreatingAccount(false)
        window.scrollTo(0, 0)
    }

    const saveLead = async () => {
        const currentParams = new URLSearchParams(window.location.search)
        const returnURLParams = new URLSearchParams({
            returned_from_mfa: 'true', // eslint-disable-line @typescript-eslint/naming-convention
            session_token: currentParams.get('session_token') || '' // eslint-disable-line @typescript-eslint/naming-convention
        })
        const mfaReturnURL = `https://${window.location.host}/checkout?${returnURLParams.toString()}`

        await saveLeadParams(['company_name', 'first_name', 'last_name', 'email', 'personal_phone_number', 'password', 'street', 'country', 'billing_country_code', 'state', 'city', 'billing_zip_code'], mfaReturnURL)
    }

    const savePaymentToken = async () => {
        if (bill?.skip_credit_card_entry) {
            return
        }

        const result = await stripe.createToken({ type: 'card' })

        if (result.error) {
            throw result.error
        } else {
            const stripePaymentToken = result.token.id
            try {
                await API.createPaymentToken(stripePaymentToken)
            } catch (error) {
                if (error.message.includes('expired')) {
                    invalidateSelectedNumber('number_expired')
                    history.push('/pick-a-number')
                }
                throw error
            }
        }
    }

    // Starts the process of google recaptcha verifying the user is a human
    const verifyHumanUser = async () => {
        // Intentionally stop the "processing" state undisabling the UI including the submit button,
        // so that if the user dismisses the challenge modal (eg. clicking outside of it) they can restart the process by submitting again.
        setProcessing(false)
        GTM.captchaStarted()

        const onError = () => {
            throw new Error('There was a problem verifying you are a human, please refresh the page and try again')
        }

        if (!recaptchaRef.current) {
            console.error('Ref to recaptcha widget not set - can not execute recaptcha')
            onError()
        }

        let token
        try {
            token = await recaptchaRef.current.executeAsync()
        } catch (error) {
            console.error('Error while executing recaptcha: ', error)
            onError()
        }

        GTM.captchaSucceeded()
        setProcessing(true)

        let response
        try {
            response = await API.verifyRecaptcha(token)
        } catch (error) {
            console.error('Error saving recaptcha token: ', error)
            onError()
        }

        if (!response?.data?.success) {
            console.error('Non-successful response from backend while trying to save recaptcha token: ', response.data)
            onError()
        }
    }

    const onRecaptchaExpired = () => {
        console.info('ReCAPTCHA Expired')
        GTM.captchaExpired()
        onError(new Error('Your ReCAPTCHA session used to verify that your a human has expired, please try again'))
    }

    const onRecaptchaError = (error: Error) => {
        console.error(`ReCAPTCHA Error: ${error.name} ${error.message}`)
        GTM.captchaError(error)
        onError(new Error('An error occured while trying to verify that your a human, please try again'))
    }

    const createAccount = async () => {
        setIsCreatingAccount(true)
        try {
            const accountDetails = await API.createAccount({ receiveSMS, receiveSMSOffers })
            setAccountDetails(accountDetails)
        } catch (error) {
            onError(error)
            GTM.transactionError(error, 'account creation')
        }
    }

    useEffect(() => {
        setDisableStepper(processing || isCreatingAccount)
    }, [processing, isCreatingAccount])

    useEffect(() => {
        // For UX reasons we're hiding the Stepper when the account is created,
        // as there is no going back to previous steps at this point
        setShowStepper(!accountDetails)
    }, [accountDetails])

    if (accountDetails) return <AccountCreated accountDetails={accountDetails}/>
    if (isCreatingAccount) return <CreatingYourAccount />

    return (
        <>
            <form id="checkout-form" name="checkout-form" className={classes.form} onSubmit={onSubmit}>
                {error &&
                    <Alert
                        data-testid="error-alert"
                        showIcon={true}
                        color={AlertColor.ERROR}
                        content={error.message}
                    />
                }

                <OrderLayout>
                    <OrderLayoutLeftColumn>
                        <BusinessInfoForm />
                        <SecureCheckoutForm
                            skipCreditCardEntry={bill?.skip_credit_card_entry || false}
                        />
                    </OrderLayoutLeftColumn>

                    <OrderLayoutRightColumn>
                        <OrderSummary
                            disabled={processing}
                        />

                        <TermsAndConditions
                            disabled={processing}

                            receiveSMS={receiveSMS}
                            setReceiveSMS={setReceiveSMS}

                            receiveSMSOffers={receiveSMSOffers}
                            setReceiveSMSOffers={setReceiveSMSOffers}
                        />

                        <div className={classes.submitContainer}>
                            <SubmitButton
                                disabled={processing || !bill || (bill?.coupon_code === 'error')}
                            >
                                {processing
                                    ? (
                                        <Spinner color="black" />
                                    )
                                    : (
                                        <>Create your account</>
                                    )}
                            </SubmitButton>
                        </div>

                        <CheckoutBadges />
                    </OrderLayoutRightColumn>
                </OrderLayout>
                <div className={classes.recaptchaContainer}>
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        size="invisible"
                        badge="bottomleft"
                        sitekey="6LcEHXAUAAAAALeg1BlVWZNcvo6LCOf_mwa8rh1P"
                        onError={onRecaptchaError}
                        onExpired={onRecaptchaExpired}
                    />
                </div>
            </form>
        </>
    )
}

export default injectStripe(Checkout)
